import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function EditarAfiliado() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Datos del afiliado
  const [afiliado, setAfiliado] = useState<any>({
    nombre: '',
    correo: '',
    telefono: '',
    biografia: '',
    anos_experiencia: 0,
    especialidad: '',
    licencia: '',
    inmobiliaria_doc_ids: [],
    redes: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    },
    sitio_web: '',
    activo: true
  });

  // Lista de inmobiliarias disponibles
  const [inmobiliarias, setInmobiliarias] = useState<any[]>([]);

  // Inmobiliaria seleccionada (solo una para el formulario)
  const [inmobiliariaSeleccionada, setInmobiliariaSeleccionada] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      // Sprint 1 (F4-006): permitir ADMIN y ASOCIADO, validando que ASOCIADO
      // solo pueda editar afiliados de su propia inmobiliaria.
      const userRol = (user as any)?.rol;
      const userInmo = (user as any)?.inmobiliaria_doc_id;
      const esAdmin = !!user && Array.isArray(user.tipo) && user.tipo.includes('admin');
      const esAsociado = userRol === 'ASOCIADO';

      if (!user || (!esAdmin && !esAsociado)) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para acceder a esta sección.'
        });
        navigate('/admin-dashboard');
        return;
      }

      if (!id) {
        Swal.fire({
          icon: 'error',
          title: 'ID no válido',
          text: 'No se proporcionó un ID de afiliado válido.'
        });
        navigate('/admin-afiliado');
        return;
      }

      try {
        // Cargar datos del afiliado
        const afiliadoDocRef = doc(db, 'asesor', id);
        const afiliadoSnap = await getDoc(afiliadoDocRef);

        if (!afiliadoSnap.exists()) {
          Swal.fire({
            icon: 'error',
            title: 'No encontrado',
            text: 'El afiliado no existe.'
          });
          navigate('/admin-afiliado');
          return;
        }

        const afiliadoData = afiliadoSnap.data();

        // Sprint 1 (F4-006): ownership check - ASOCIADO solo edita afiliados de su inmobiliaria.
        if (!esAdmin && esAsociado) {
          const inmoIds: string[] = Array.isArray(afiliadoData.inmobiliaria_doc_ids)
            ? afiliadoData.inmobiliaria_doc_ids
            : [];
          if (!userInmo || !inmoIds.includes(userInmo)) {
            Swal.fire({
              icon: 'error',
              title: 'No autorizado',
              text: 'Este afiliado no pertenece a tu inmobiliaria.',
            });
            navigate('/admin-afiliado');
            return;
          }
        }

        // Agentes MLS: redirigir a vista detalle
        if (afiliadoData.fuente === 'flexmls' || afiliadoData.creado_auto) {
          Swal.fire({
            icon: 'info',
            title: 'Afiliado MLS',
            text: 'Los datos de este afiliado se actualizan desde el MLS. Usa la vista de detalle para verificarlo o asignar propiedades.',
            confirmButtonText: 'Ir a vista de detalle'
          }).then(() => {
            navigate(`/ver-admin-afiliado/${id}`);
          });
          return;
        }

        // Establecer datos del afiliado
        setAfiliado({
          nombre: afiliadoData.nombre || '',
          correo: afiliadoData.correo || '',
          telefono: afiliadoData.telefono || '',
          biografia: afiliadoData.biografia || '',
          anos_experiencia: afiliadoData.anos_experiencia || 0,
          especialidad: afiliadoData.especialidad || '',
          licencia: afiliadoData.licencia || '',
          inmobiliaria_doc_ids: afiliadoData.inmobiliaria_doc_ids || [],
          redes: {
            facebook: afiliadoData.redes?.facebook || '',
            instagram: afiliadoData.redes?.instagram || '',
            twitter: afiliadoData.redes?.twitter || '',
            linkedin: afiliadoData.redes?.linkedin || ''
          },
          sitio_web: afiliadoData.sitio_web || '',
          activo: afiliadoData.activo !== false
        });

        // Si tiene inmobiliarias asignadas, seleccionar la primera
        if (afiliadoData.inmobiliaria_doc_ids && Array.isArray(afiliadoData.inmobiliaria_doc_ids) && afiliadoData.inmobiliaria_doc_ids.length > 0) {
          setInmobiliariaSeleccionada(afiliadoData.inmobiliaria_doc_ids[0]);
        }

        // Cargar todas las inmobiliarias disponibles
        const inmobiliariasSnap = await getDocs(collection(db, 'inmobiliaria'));
        const inmoList = inmobiliariasSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || 'Sin nombre'
        }));
        setInmobiliarias(inmoList);

      } catch (err) {
        console.error('Error al cargar afiliado:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al cargar los datos del afiliado.'
        });
        navigate('/admin-afiliado');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('redes.')) {
      const redName = name.split('.')[1];
      setAfiliado((prev: any) => ({
        ...prev,
        redes: {
          ...prev.redes,
          [redName]: value
        }
      }));
    } else {
      setAfiliado((prev: any) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);

    try {
      // Validaciones básicas
      if (!afiliado.nombre.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Nombre requerido',
          text: 'El nombre del afiliado es obligatorio.'
        });
        setGuardando(false);
        return;
      }

      if (!afiliado.correo.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Correo requerido',
          text: 'El correo del afiliado es obligatorio.'
        });
        setGuardando(false);
        return;
      }

      // Preparar datos para guardar
      const dataToSave: any = {
        nombre: afiliado.nombre.trim(),
        correo: afiliado.correo.trim(),
        telefono: afiliado.telefono.trim(),
        biografia: afiliado.biografia.trim(),
        anos_experiencia: parseInt(afiliado.anos_experiencia) || 0,
        especialidad: afiliado.especialidad.trim(),
        licencia: afiliado.licencia.trim(),
        inmobiliaria_doc_ids: inmobiliariaSeleccionada ? [inmobiliariaSeleccionada] : [],
        redes: {
          facebook: afiliado.redes.facebook.trim(),
          instagram: afiliado.redes.instagram.trim(),
          twitter: afiliado.redes.twitter.trim(),
          linkedin: afiliado.redes.linkedin.trim()
        },
        sitio_web: afiliado.sitio_web.trim(),
        activo: afiliado.activo
      };

      // Actualizar en Firestore
      const afiliadoDocRef = doc(db, 'asesor', id!);
      await updateDoc(afiliadoDocRef, dataToSave);

      Swal.fire({
        icon: 'success',
        title: 'Guardado exitoso',
        text: 'Los datos del afiliado se actualizaron correctamente.'
      });

      navigate(`/ver-admin-afiliado/${id}`);
    } catch (err) {
      console.error('Error al guardar afiliado:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo guardar los cambios. Intenta nuevamente.'
      });
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando información del afiliado...</p>
      </div>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Editar Afiliado</h2>
          <span className="ipn-subtitle">{afiliado.nombre}</span>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Administración de Afiliados', path: '/admin-afiliado' },
              { label: afiliado.nombre, path: `/ver-admin-afiliado/${id}` },
              { label: 'Editar' }
            ]}
          />

          <form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-user me-2"></i>Información Básica</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="nombre" className="form-label">Nombre completo *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="nombre"
                      name="nombre"
                      value={afiliado.nombre}
                      onChange={handleInputChange}
                      placeholder="Nombre completo del afiliado"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="correo" className="form-label">Correo electrónico *</label>
                    <input
                      type="email"
                      className="form-control"
                      id="correo"
                      name="correo"
                      value={afiliado.correo}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.com"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="telefono" className="form-label">Teléfono</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="telefono"
                      name="telefono"
                      value={afiliado.telefono}
                      onChange={handleInputChange}
                      placeholder="Número de teléfono"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="anos_experiencia" className="form-label">Años de experiencia</label>
                    <input
                      type="number"
                      className="form-control"
                      id="anos_experiencia"
                      name="anos_experiencia"
                      value={afiliado.anos_experiencia}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="especialidad" className="form-label">Especialidad</label>
                    <input
                      type="text"
                      className="form-control"
                      id="especialidad"
                      name="especialidad"
                      value={afiliado.especialidad}
                      onChange={handleInputChange}
                      placeholder="Especialidad del asesor"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="licencia" className="form-label">Licencia</label>
                    <input
                      type="text"
                      className="form-control"
                      id="licencia"
                      name="licencia"
                      value={afiliado.licencia}
                      onChange={handleInputChange}
                      placeholder="Número de licencia"
                    />
                  </div>

                  <div className="col-12">
                    <label htmlFor="biografia" className="form-label">Biografía</label>
                    <textarea
                      className="form-control"
                      id="biografia"
                      name="biografia"
                      rows={4}
                      value={afiliado.biografia}
                      onChange={handleInputChange}
                      placeholder="Biografía del afiliado"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Inmobiliaria */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-building me-2"></i>Inmobiliaria</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label htmlFor="inmobiliaria" className="form-label">Asignar a Inmobiliaria</label>
                    <select
                      className="form-select"
                      id="inmobiliaria"
                      value={inmobiliariaSeleccionada}
                      onChange={(e) => setInmobiliariaSeleccionada(e.target.value)}
                    >
                      <option value="">Sin inmobiliaria</option>
                      {inmobiliarias.map(inmo => (
                        <option key={inmo.id} value={inmo.id}>
                          {inmo.nombre}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Los afiliados pueden pertenecer a una o más inmobiliarias
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Redes Sociales */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-share-alt me-2"></i>Redes Sociales y Sitio Web</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="redes.facebook" className="form-label">
                      <i className="fab fa-facebook text-primary me-2"></i>Facebook
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.facebook"
                      name="redes.facebook"
                      value={afiliado.redes.facebook}
                      onChange={handleInputChange}
                      placeholder="https://facebook.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.instagram" className="form-label">
                      <i className="fab fa-instagram text-danger me-2"></i>Instagram
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.instagram"
                      name="redes.instagram"
                      value={afiliado.redes.instagram}
                      onChange={handleInputChange}
                      placeholder="https://instagram.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.twitter" className="form-label">
                      <i className="fab fa-twitter text-info me-2"></i>Twitter
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.twitter"
                      name="redes.twitter"
                      value={afiliado.redes.twitter}
                      onChange={handleInputChange}
                      placeholder="https://twitter.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.linkedin" className="form-label">
                      <i className="fab fa-linkedin text-primary me-2"></i>LinkedIn
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.linkedin"
                      name="redes.linkedin"
                      value={afiliado.redes.linkedin}
                      onChange={handleInputChange}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="sitio_web" className="form-label">
                      <i className="fas fa-globe text-secondary me-2"></i>Sitio Web
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="sitio_web"
                      name="sitio_web"
                      value={afiliado.sitio_web}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(`/ver-admin-afiliado/${id}`)}
                    disabled={guardando}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={guardando}
                  >
                    {guardando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Guardar Cambios
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
